import React, { useState } from 'react';
import { BankAccount, Debt } from '../types';
import { Building2, X } from 'lucide-react';

interface PlaidAccountMapperProps {
  plaidAccounts: any[];
  existingAccounts: BankAccount[];
  existingDebts: Debt[];
  institutionName: string;
  onSave: (mappings: Record<string, { id: string; type: 'bank_account' | 'debt' }>) => Promise<void>;
  onCancel: () => void;
}

export default function PlaidAccountMapper({ plaidAccounts, existingAccounts, existingDebts, institutionName, onSave, onCancel }: PlaidAccountMapperProps) {
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Helper to determine if an existing account is a debt
  const isDebt = (id: string) => existingDebts.some(d => d.id === id);

  const handleSave = async () => {
    setSaving(true);
    try {
      const finalMappings: Record<string, { id: string; type: 'bank_account' | 'debt' }> = {};
      for (const [plaidId, internalId] of Object.entries(mappings) as [string, string][]) {
        if (internalId && internalId !== 'ignore') {
          finalMappings[plaidId] = {
            id: internalId,
            type: isDebt(internalId) ? 'debt' : 'bank_account'
          };
        }
      }
      await onSave(finalMappings);
    } catch (err) {
      console.error(err);
      alert('Failed to save mappings.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-slate-600" />
            <h2 className="font-semibold text-slate-800">Map {institutionName} Accounts</h2>
          </div>
          <button onClick={onCancel} className="p-1 hover:bg-slate-200 rounded transition-colors text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto">
          <p className="text-sm text-slate-600 mb-6">
            We found {plaidAccounts.length} account(s) at {institutionName}. 
            Select which existing account in your Budget app each one corresponds to, or choose "Do not import" to ignore it.
          </p>

          <div className="space-y-4">
            {plaidAccounts.map(plaidAcc => (
              <div key={plaidAcc.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-white">
                <div>
                  <p className="font-medium text-slate-800">{plaidAcc.name} <span className="text-slate-400 font-normal">...{plaidAcc.mask}</span></p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">{plaidAcc.subtype || plaidAcc.type}</p>
                </div>
                <div className="w-64">
                  <select
                    className="w-full border-slate-200 rounded-md text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    value={mappings[plaidAcc.id] || ''}
                    onChange={(e) => setMappings(prev => ({ ...prev, [plaidAcc.id]: e.target.value }))}
                  >
                    <option value="">-- Select an account --</option>
                    <option value="ignore">Do not import</option>
                    <optgroup label="Bank Accounts">
                      {existingAccounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name} {acc.last4 ? `(...${acc.last4})` : ''}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Debts & Credit Cards">
                      {existingDebts.map(debt => (
                        <option key={debt.id} value={debt.id}>{debt.name} {debt.last4 ? `(...${debt.last4})` : ''}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save & Sync'}
          </button>
        </div>
      </div>
    </div>
  );
}
